import {
  usePlaybackCurrentTs,
  usePlaybackPlay,
  usePlaybackRange,
} from '@app/core/playback/hooks/usePlayback';
import { Fragment } from '@app/modules/fragments/types';
import { DateTimeStr, toTimestamp } from '@mi-front/core';
import { useCallback } from 'react';
import { TranscriptionSegmentData, TranscriptionWordData } from '../lib/transcription.types';

/**
 * Playback sync for the V2 transcript: word/segment seek and active block/word highlighting
 * using `adjustedEnd` on segments/words so gaps on the timeline keep the previous block/word highlighted.
 */
export function useTranscriptionPlaybackSync(fragment: Fragment) {
  const currentTs = usePlaybackCurrentTs();
  const play = usePlaybackPlay();
  const range = usePlaybackRange<true>();

  /**
   * Seeks to `timeSeconds` (seconds from chunk start) if the fragment has a matching chunk.
   */
  const seekToTimeSeconds = useCallback(
    (timeSeconds: number, chunkTime: DateTimeStr) => {
      const chunk = fragment.chunks.find((c) => toTimestamp(c.from) <= toTimestamp(chunkTime));
      if (chunk) {
        play({
          startTs: range.from + timeSeconds * 1000,
          playAfterSeek: true,
        });
      }
    },
    [fragment.chunks, play, range.from],
  );

  const isBlockActive = useCallback(
    (segment: TranscriptionSegmentData) => {
      const ts = currentTs ?? 0;
      const segmentStart = toTimestamp(range.from + segment.start * 1000);
      const segmentEnd = toTimestamp(range.from + segment.adjustedEnd * 1000);
      return segmentStart <= ts && segmentEnd > ts;
    },
    [currentTs, range.from],
  );

  const isWordHighlighted = useCallback(
    (word: TranscriptionWordData, blockActive: boolean) => {
      if (!blockActive) {
        return false;
      }
      const ts = currentTs ?? 0;
      const wordStart = range.from + word.start * 1000;
      const wordEnd = range.from + word.adjustedEnd * 1000;
      return wordStart <= ts && wordEnd > ts;
    },
    [currentTs, range.from],
  );

  return {
    currentTs,
    seekToTimeSeconds,
    isBlockActive,
    isWordHighlighted,
  };
}
